import camelCase from 'lodash/camelCase';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import snakeCase from 'lodash/snakeCase';

type UnknownObject = { [s: string]: unknown };

const transformKeysDeep = (input: unknown, transformer: (key: string) => string, onlyFirstLevel?: boolean): unknown => {
  if (isArray(input)) {
    return input.map((child) => transformKeysDeep(child, transformer));
  }

  if (isPlainObject(input)) {
    return reduce(
      input as UnknownObject,
      (result: UnknownObject, value: unknown, key: string) => ({
        ...result,
        [transformer(key)]: onlyFirstLevel ? value : transformKeysDeep(value, transformer),
      }),
      {},
    );
  }

  return input;
};

export const camelCaseKeys = <T = unknown,>(input: unknown): T => transformKeysDeep(input, camelCase) as T;

export const snakeCaseKeys = <T = unknown,>(input: unknown): T => transformKeysDeep(input, snakeCase) as T;
export const snakeCaseKeysOnlyFirstLevel = <T = unknown,>(input: unknown): T =>
  transformKeysDeep(input, snakeCase, true) as T;

export const ignoreFirstLevelKeys = <T = unknown,>(
  transformer: (input: unknown) => T,
  ignoredKeys: string[],
): ((input: unknown) => T) => {
  if (!ignoredKeys) {
    return transformer;
  }

  return (input: unknown) => {
    if (!isPlainObject(input)) {
      return transformer(input);
    }

    return {
      ...transformer(omit(input as UnknownObject, ignoredKeys)),
      ...pick(input as UnknownObject, ignoredKeys),
    } as T;
  };
};
